/**
 * PersonalityChat - AI-powered personality assessment through natural conversation
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    IconButton,
    Avatar,
    Chip,
    LinearProgress,
    CircularProgress,
    Alert,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Send,
    Psychology,
    Person,
    CheckCircle,
    RadioButtonUnchecked,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../hooks/redux';
import { websocketService } from '../../services/websocketService';
import { personalityApi } from '../../services/personalityApi';
import {
    PersonalityChatMessage,
    PersonalityProgressUpdate,
    PersonalityProfile,
} from '../../types/personality';

interface PersonalityChatProps {
    onComplete: (profile: PersonalityProfile) => void;
}

const TRAIT_LABELS: Record<string, string> = {
    openness: 'Openness',
    conscientiousness: 'Conscientiousness',
    extraversion: 'Extraversion',
    agreeableness: 'Agreeableness',
    neuroticism: 'Emotional Stability',
    values: 'Values',
    communication: 'Communication',
};

const PersonalityChat: React.FC<PersonalityChatProps> = ({ onComplete }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { token } = useAppSelector(state => state.auth);
    const { t, i18n } = useTranslation();

    const [messages, setMessages] = useState<PersonalityChatMessage[]>([]);
    const [inputText, setInputText] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isTyping, setIsTyping] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState<PersonalityProgressUpdate | null>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Auto-scroll to bottom
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    // Initialize assessment
    useEffect(() => {
        let mounted = true;

        const init = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Start assessment session
                const session = await personalityApi.startAssessment(i18n.language);
                if (!mounted) return;
                setSessionId(session.session_id);

                // Connect WebSocket
                const connected = await websocketService.connectToSession(session.session_id, token || '');
                if (!mounted) return;

                if (!connected) {
                    setError('Failed to connect to assessment server');
                    setIsLoading(false);
                    return;
                }

                setIsConnected(true);

                // Register event handlers
                websocketService.on('ai_message', handleAiMessage);
                websocketService.on('progress_update', handleProgressUpdate);
                websocketService.on('assessment_complete', handleAssessmentComplete);
                websocketService.on('session_status_change', handleSessionStatus);
                websocketService.on('error', handleError);

                // Trigger greeting
                websocketService.startAssessment();
                setIsLoading(false);
            } catch (err: any) {
                if (!mounted) return;
                setError(err?.message || 'Failed to start assessment');
                setIsLoading(false);
            }
        };

        init();

        return () => {
            mounted = false;
            websocketService.off('ai_message', handleAiMessage);
            websocketService.off('progress_update', handleProgressUpdate);
            websocketService.off('assessment_complete', handleAssessmentComplete);
            websocketService.off('session_status_change', handleSessionStatus);
            websocketService.off('error', handleError);
            websocketService.disconnect();
        };
    }, [token]);

    const handleAiMessage = useCallback((data: any) => {
        const msg = data.message || data;
        const newMsg: PersonalityChatMessage = {
            message_id: msg.message_id || `msg_${Date.now()}`,
            sender: 'interviewer',
            content: msg.content || '',
            timestamp: msg.timestamp || new Date().toISOString(),
        };
        setMessages(prev => [...prev, newMsg]);
        setIsTyping(false);
    }, []);

    const handleProgressUpdate = useCallback((data: any) => {
        setProgress(data.progress || data);
    }, []);

    const handleAssessmentComplete = useCallback((data: any) => {
        const profile = data.profile;
        if (profile) {
            onComplete(profile as PersonalityProfile);
        }
    }, [onComplete]);

    const handleSessionStatus = useCallback((data: any) => {
        if (data.status === 'completed') {
            setIsTyping(false);
        }
    }, []);

    const handleError = useCallback((data: any) => {
        setError(data.message || 'An error occurred');
        setIsTyping(false);
    }, []);

    const handleSend = () => {
        const text = inputText.trim();
        if (!text || !isConnected) return;

        // Add user message to UI
        const userMsg: PersonalityChatMessage = {
            message_id: `user_${Date.now()}`,
            sender: 'user',
            content: text,
            timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, userMsg]);
        setInputText('');
        setIsTyping(true);

        // Send via WebSocket
        websocketService.sendUserMessage(text);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                    {t('personality.chat.preparing')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('personality.chat.connecting')}
                </Typography>
            </Box>
        );
    }

    if (error && messages.length === 0) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    const completionPercent = progress?.completion_percentage || 0;

    return (
        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={2} sx={{ height: '70vh', minHeight: 500 }}>
            {/* Chat Area */}
            <Paper
                elevation={3}
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderRadius: 2,
                }}
            >
                {/* Messages */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        p: 2,
                        bgcolor: 'grey.50',
                    }}
                >
                    <AnimatePresence initial={false}>
                        {messages.map((msg) => (
                            <motion.div
                                key={msg.message_id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Box
                                    display="flex"
                                    justifyContent={msg.sender === 'user' ? 'flex-end' : 'flex-start'}
                                    mb={1.5}
                                >
                                    {msg.sender === 'interviewer' && (
                                        <Avatar sx={{ bgcolor: 'primary.main', mr: 1, width: 36, height: 36 }}>
                                            <Psychology fontSize="small" />
                                        </Avatar>
                                    )}
                                    <Paper
                                        sx={{
                                            p: 1.5,
                                            maxWidth: '75%',
                                            bgcolor: msg.sender === 'user' ? 'primary.main' : 'white',
                                            color: msg.sender === 'user' ? 'white' : 'text.primary',
                                            borderRadius: 2,
                                            borderTopLeftRadius: msg.sender === 'interviewer' ? 0 : 2,
                                            borderTopRightRadius: msg.sender === 'user' ? 0 : 2,
                                        }}
                                    >
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {msg.content}
                                        </Typography>
                                    </Paper>
                                    {msg.sender === 'user' && (
                                        <Avatar sx={{ bgcolor: 'secondary.main', ml: 1, width: 36, height: 36 }}>
                                            <Person fontSize="small" />
                                        </Avatar>
                                    )}
                                </Box>
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {/* Typing indicator */}
                    {isTyping && (
                        <Box display="flex" alignItems="center" mb={1}>
                            <Avatar sx={{ bgcolor: 'primary.main', mr: 1, width: 36, height: 36 }}>
                                <Psychology fontSize="small" />
                            </Avatar>
                            <Paper sx={{ p: 1.5, borderRadius: 2, borderTopLeftRadius: 0 }}>
                                <Box display="flex" gap={0.5}>
                                    {[0, 1, 2].map(i => (
                                        <motion.div
                                            key={i}
                                            animate={{ y: [0, -4, 0] }}
                                            transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.2 }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    bgcolor: 'grey.400',
                                                }}
                                            />
                                        </motion.div>
                                    ))}
                                </Box>
                            </Paper>
                        </Box>
                    )}

                    <div ref={messagesEndRef} />
                </Box>

                {/* Input Area */}
                <Box
                    sx={{
                        p: 1.5,
                        borderTop: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        display: 'flex',
                        gap: 1,
                    }}
                >
                    <TextField
                        inputRef={inputRef}
                        fullWidth
                        variant="outlined"
                        placeholder={t('personality.chat.placeholder')}
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        size="small"
                        multiline
                        maxRows={3}
                        disabled={!isConnected}
                    />
                    <IconButton
                        color="primary"
                        onClick={handleSend}
                        disabled={!inputText.trim() || !isConnected}
                        sx={{ alignSelf: 'flex-end' }}
                    >
                        <Send />
                    </IconButton>
                </Box>
            </Paper>

            {/* Progress Sidebar */}
            {!isMobile && (
                <Paper
                    elevation={3}
                    sx={{
                        width: 260,
                        p: 2,
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        {t('personality.chat.progress')}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2" color="text.secondary">
                                {t('personality.chat.overall')}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {Math.round(completionPercent)}%
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={completionPercent}
                            sx={{ height: 8, borderRadius: 4 }}
                        />
                    </Box>

                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t('personality.chat.dimensions')}
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={1}>
                        {Object.entries(TRAIT_LABELS).map(([key, label]) => {
                            const covered = progress?.traits_covered?.[key] || false;
                            return (
                                <Box key={key} display="flex" alignItems="center" gap={1}>
                                    {covered ? (
                                        <CheckCircle color="success" fontSize="small" />
                                    ) : (
                                        <RadioButtonUnchecked color="disabled" fontSize="small" />
                                    )}
                                    <Typography
                                        variant="body2"
                                        color={covered ? 'text.primary' : 'text.secondary'}
                                        fontWeight={covered ? 'bold' : 'normal'}
                                    >
                                        {t(`personality.chat.trait.${key}`)}
                                    </Typography>
                                    {covered && (
                                        <Chip label={t('personality.chat.done')} size="small" color="success" variant="outlined" sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }} />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ mt: 'auto', pt: 2 }}>
                        <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                            {t('personality.chat.messagesExchanged', { count: progress?.messages_exchanged || 0 })}
                        </Typography>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default PersonalityChat;
